import { memo, useReducer } from 'react';

import { useFormik } from 'formik';
import { Download, SquarePen, Trash2 } from 'lucide-react';
import { omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { LotteryForm } from '../form';

import css from './LotteryCard.module.css';

import { lotteriesService } from 'v2/api/lotteriesService';
import { MarkedAutocomplete } from 'v2/components/autocomplete/markedAutocomplete/MarkedAutocomplete';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { Switch } from 'v2/components/switch/Switch';
import { LotteryModel } from 'v2/types/LotteryModel';
import { handleHttpError } from 'v2/utils';

interface LotteryCardProps {
  lottery: LotteryModel;
  number: number;
  onRevalidate: VoidFunction;
}

export const LotteryCard = memo<LotteryCardProps>(({ lottery, number, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const formik = useFormik<LotteryForm>({
    initialValues: omit(lottery, ['id', 'participants']),
    onSubmit: (values) => {
      triggerUpdate({ ...values, info: undefined });
    },
    enableReinitialize: true,
  });

  const { trigger: triggerActive } = useSWRMutation(
    ['setActiveLottery', lottery.id],
    lotteriesService.setActiveLottery,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateLottery', lottery.id],
    lotteriesService.updateLottery,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The lottery has been successfully updated');
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(['deleteLottery', lottery.id], lotteriesService.deleteLottery, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerExport } = useSWRMutation(
    ['exportLotteryParticipants', lottery.id],
    lotteriesService.exportLotteryParticipants,
    { onError: handleHttpError }
  );

  const handleToggleActive = () => {
    triggerActive(!lottery.isActive);
  };

  const handleAutocompleteChange = (key: keyof LotteryForm) => (v: string[]) => {
    formik.setFieldValue(key, v);
  };

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  return (
    <div className={css.lotteryCard}>
      <div className={css.lotteryCardHeader}>
        <p className={css.lotteryCardNumber}>{number}</p>

        <Switch name="isActive" checked={lottery.isActive} onChange={handleToggleActive} label="Active" />
      </div>

      <Divider />

      <div className={css.lotteryCardActions}>
        <Tooltip title="Export Participants" placement="top">
          <IconButton onClick={() => triggerExport()} color="primary">
            <Download width={18} height={18} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Edit" placement="top">
          <IconButton onClick={setOpen} color="warning">
            <SquarePen width={18} height={18} />
          </IconButton>
        </Tooltip>

        <Popconfirm message="Are you sure that you want to delete this draw?" onConfirm={triggerDelete}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={18} height={18} />
            </IconButton>
          </Tooltip>
        </Popconfirm>

        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
          <DialogTitle>Edit Lottery</DialogTitle>

          <DialogContent>
            <form id="lotteryForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
              <MarkedAutocomplete
                label="Handled Participants"
                value={formik.values.handledParticipants}
                options={formik.values.handledParticipants}
                onChange={handleAutocompleteChange('handledParticipants')}
                required
              />

              <MarkedAutocomplete
                label="Verified Participants"
                value={formik.values.verifiedParticipants}
                options={formik.values.verifiedParticipants}
                onChange={handleAutocompleteChange('verifiedParticipants')}
                required
              />

              <MarkedAutocomplete
                label="RS Referrals"
                value={formik.values.realSlotsReferrals}
                options={formik.values.realSlotsReferrals}
                onChange={handleAutocompleteChange('realSlotsReferrals')}
                required
              />

              <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />
            </form>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button form="lotteryForm" loading={isMutating} type="submit" variant="contained">
              Edit
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
});

LotteryCard.displayName = 'LotteryCard';
